export interface WorkbenchCodeFile {
  readonly path: string;
  readonly content: string;
}

export interface WorkbenchCodeProject {
  readonly format: "snowiespace-code-project-v1";
  readonly framework: "snowiespace-code-app-v1";
  readonly files: readonly WorkbenchCodeFile[];
}

export interface WorkbenchCodeManifest {
  readonly name: string;
  readonly version: string;
  readonly purpose: string;
  readonly entry: "index.html";
  readonly style: "styles.css";
  readonly ui: "app.js";
  readonly actions: "actions.js";
  readonly schema: "schema.json";
  readonly data: "shared-space-sqlite";
}

export interface WorkbenchCodeTable {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly fields: readonly { readonly id: string; readonly label: string; readonly type: string; readonly required?: boolean }[];
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

export function isCodeAppSource(source: string): boolean {
  try {
    const value = record(JSON.parse(source));
    return value?.format === "snowiespace-code-project-v1" && value.framework === "snowiespace-code-app-v1";
  } catch {
    return false;
  }
}

export function parseCodeAppProject(source: string): { project?: WorkbenchCodeProject; issue?: string } {
  let value: Record<string, unknown> | undefined;
  try {
    value = record(JSON.parse(source));
  } catch {
    return { issue: "The project source envelope is not valid JSON. Workbench preserves your files while you correct it." };
  }
  if (!value || value.format !== "snowiespace-code-project-v1" || value.framework !== "snowiespace-code-app-v1" || !Array.isArray(value.files)) return { issue: "This source is not a Snowiespace code App project." };
  const files: WorkbenchCodeFile[] = [];
  const paths = new Set<string>();
  for (const raw of value.files) {
    const file = record(raw);
    if (!file || typeof file.path !== "string" || typeof file.content !== "string" || paths.has(file.path)) return { issue: "Every project file needs a unique path and text content." };
    paths.add(file.path);
    files.push({ path: file.path, content: file.content });
  }
  for (const path of ["snowiespace.app.json", "index.html", "styles.css", "app.js", "actions.js", "schema.json"]) if (!paths.has(path)) return { issue: `The project is missing required file ${path}.` };
  return { project: { format: "snowiespace-code-project-v1", framework: "snowiespace-code-app-v1", files } };
}

export function serializeCodeAppProject(project: WorkbenchCodeProject): string {
  return `${JSON.stringify(project, null, 2)}\n`;
}

export function updateCodeAppFile(project: WorkbenchCodeProject, path: string, content: string): WorkbenchCodeProject {
  return { ...project, files: project.files.map((file) => file.path === path ? { ...file, content } : file) };
}

export function codeAppManifest(project?: WorkbenchCodeProject): WorkbenchCodeManifest | undefined {
  const source = project?.files.find((file) => file.path === "snowiespace.app.json")?.content;
  if (!source) return undefined;
  try {
    const value = record(JSON.parse(source));
    if (!value || typeof value.name !== "string" || typeof value.version !== "string" || typeof value.purpose !== "string" || value.entry !== "index.html" || value.style !== "styles.css" || value.ui !== "app.js" || value.actions !== "actions.js" || value.schema !== "schema.json" || value.data !== "shared-space-sqlite") return undefined;
    return value as unknown as WorkbenchCodeManifest;
  } catch {
    return undefined;
  }
}

export function codeAppTables(project?: WorkbenchCodeProject): readonly WorkbenchCodeTable[] {
  const source = project?.files.find((file) => file.path === "schema.json")?.content;
  if (!source) return [];
  try {
    const value = record(JSON.parse(source));
    if (value?.version !== 1 || !Array.isArray(value.tables)) return [];
    return value.tables.flatMap((raw) => {
      const table = record(raw);
      if (!table || typeof table.id !== "string" || typeof table.title !== "string" || !Array.isArray(table.fields)) return [];
      const fields = table.fields.flatMap((rawField) => {
        const field = record(rawField);
        return field && typeof field.id === "string" && typeof field.label === "string" && typeof field.type === "string"
          ? [{ id: field.id, label: field.label, type: field.type, ...(typeof field.required === "boolean" ? { required: field.required } : {}) }]
          : [];
      });
      return [{ id: table.id, title: table.title, ...(typeof table.description === "string" ? { description: table.description } : {}), fields }];
    });
  } catch {
    return [];
  }
}

export function personalizeWorkbenchStarter(source: string, name: string, purpose: string): string {
  const parsed = parseCodeAppProject(source);
  if (parsed.project) {
    const manifestFile = parsed.project.files.find((file) => file.path === "snowiespace.app.json")!;
    const manifest = record(JSON.parse(manifestFile.content)) ?? {};
    return serializeCodeAppProject(updateCodeAppFile(parsed.project, "snowiespace.app.json", `${JSON.stringify({ ...manifest, name: name.trim(), purpose: purpose.trim() }, null, 2)}\n`));
  }
  const value = JSON.parse(source) as Record<string, unknown>;
  return `${JSON.stringify({ ...value, name: name.trim(), purpose: purpose.trim() }, null, 2)}\n`;
}

export function codeAppLanguage(path: string): string {
  if (path.endsWith(".js")) return "JavaScript";
  if (path.endsWith(".css")) return "CSS";
  if (path.endsWith(".html")) return "HTML";
  if (path.endsWith(".json")) return "JSON";
  return "Text";
}

export function codeAppLocalIssue(source: string): string | undefined {
  const parsed = parseCodeAppProject(source);
  if (parsed.issue) return parsed.issue;
  const manifest = codeAppManifest(parsed.project);
  if (!manifest) return "snowiespace.app.json is invalid or does not bind the required project entries.";
  if (!/^\d+\.\d+\.\d+$/.test(manifest.version)) return "The App version in snowiespace.app.json must use major.minor.patch.";
  const actions = parsed.project!.files.find((file) => file.path === "actions.js")!.content;
  if (!actions.includes("snowiespace.actions.define(")) return "actions.js must register at least one custom App action.";
  return undefined;
}
